// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  Flex,
  Image,
  Link,
  Checkbox,
  Divider,
  HStack,
} from '@chakra-ui/react';
import { supabase } from '../config/supabaseClient';
import logo from '../assets/logo.png';
import { FcGoogle } from 'react-icons/fc';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Verifica se há um parâmetro de erro na URL (redirecionamento de OAuth)
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const error = query.get('error');
    const errorDescription = query.get('error_description');

    if (error) {
      toast({
        title: 'Erro na autenticação',
        description: errorDescription || 'Houve um problema durante o login. Por favor, tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [location, toast]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Verificação explícita do usuário
      if (!data || !data.user) {
        throw new Error('Falha ao autenticar. Tente novamente.');
      }

      // Login bem-sucedido
      toast({
        title: 'Login realizado com sucesso!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navegação direta para o dashboard
      navigate('/dashboard', { replace: true });

    } catch (error) {
      toast({
        title: 'Erro no login',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Em Login.jsx e Signup.jsx, modificar:
  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth-redirect'
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao fazer login com Google:', error);
      toast({
        title: 'Erro ao fazer login com Google',
        description: error.message || 'Ocorreu um erro ao tentar fazer login com o Google. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setGoogleLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <Flex direction="column" align="center" mb={8}>
        <Image src={logo} alt="Draw Your Goods" height="60px" mb={4} />
        <Heading size="lg" textAlign="center" color="brand.primary">
          Entre na sua conta
        </Heading>
        <Text mt={2} textAlign="center" color="gray.600">
          Transforme suas memórias em arte para colorir
        </Text>
      </Flex>

      <Box p={8} borderRadius="lg" boxShadow="md" bg="white">
        {/* Botão de login com Google */}
        <Button
          w="100%"
          variant="outline"
          leftIcon={<FcGoogle size={20} />}
          onClick={handleGoogleSignIn}
          mb={6}
          isLoading={googleLoading}
          loadingText="Conectando"
          height="48px"
          fontSize="md"
          iconSpacing={3}
        >
          Entrar com Google
        </Button>

        <HStack my={6}>
          <Divider />
          <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">ou com email</Text>
          <Divider />
        </HStack>

        <form onSubmit={handleLogin}>
          <Stack spacing={4}>
            <FormControl id="email" isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
              />
            </FormControl>

            <FormControl id="password" isRequired>
              <FormLabel>Senha</FormLabel>
              <InputGroup>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Flex justify="space-between" align="center">
              <Checkbox
                colorScheme="blue"
                isChecked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              >
                Lembrar de mim
              </Checkbox>
              <Link color="brand.primary" href="/forgot-password">
                Esqueceu a senha?
              </Link>
            </Flex>

            <Button
              type="submit"
              colorScheme="blue"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              color="white"
              size="lg"
              w="100%"
              mt={6}
              isLoading={loading}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
            >
              Entrar
            </Button>
          </Stack>
        </form>

        <Text mt={6} textAlign="center">
          Ainda não tem uma conta?{' '}
          <Link color="brand.primary" href="/signup">
            Cadastre-se
          </Link>
        </Text>
      </Box>
    </Container>
  );
};

export default Login;